import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import type { DeviceChannels } from "../view-model";

export type ChannelValues = {
  [deviceId: string]: {
    channels: DeviceChannels | null;
    isLoading: boolean;
    error: Error | null;
    lastUpdated: number;
  };
};

export interface ChannelsStore {
  // State
  channelValues: ChannelValues;

  // Actions
  setChannelValues: (deviceId: string, channels: DeviceChannels | null) => void;
  setChannelLoading: (deviceId: string, isLoading: boolean) => void;
  setChannelError: (deviceId: string, error: Error | null) => void;
  clearChannelValues: (deviceId: string) => void;

  // Computed selectors
  getChannelValues: (deviceId: string) => DeviceChannels | null;
  getChannelLoading: (deviceId: string) => boolean;
  getChannelError: (deviceId: string) => Error | null;
  getChannelLastUpdated: (deviceId: string) => number;
}

export const useChannelsStore = create<ChannelsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      channelValues: {},

      // Actions
      setChannelValues: (deviceId: string, channels: DeviceChannels | null) => {
        set(
          (state) => ({
            channelValues: {
              ...state.channelValues,
              [deviceId]: {
                channels,
                isLoading: false,
                error: null,
                lastUpdated: Date.now(),
              },
            },
          }),
          false,
          "setChannelValues"
        );
      },

      setChannelLoading: (deviceId: string, isLoading: boolean) => {
        set(
          (state) => ({
            channelValues: {
              ...state.channelValues,
              [deviceId]: {
                channels: state.channelValues[deviceId]?.channels || null,
                isLoading,
                error: null,
                lastUpdated:
                  state.channelValues[deviceId]?.lastUpdated || Date.now(),
              },
            },
          }),
          false,
          "setChannelLoading"
        );
      },

      setChannelError: (deviceId: string, error: Error | null) => {
        set(
          (state) => ({
            channelValues: {
              ...state.channelValues,
              [deviceId]: {
                channels: state.channelValues[deviceId]?.channels || null,
                isLoading: false,
                error,
                lastUpdated:
                  state.channelValues[deviceId]?.lastUpdated || Date.now(),
              },
            },
          }),
          false,
          "setChannelError"
        );
      },

      clearChannelValues: (deviceId: string) => {
        set(
          (state) => {
            const newChannelValues = { ...state.channelValues };
            delete newChannelValues[deviceId];
            return { channelValues: newChannelValues };
          },
          false,
          "clearChannelValues"
        );
      },

      // Computed selectors
      getChannelValues: (deviceId: string) => {
        const { channelValues } = get();
        return channelValues[deviceId]?.channels || null;
      },

      getChannelLoading: (deviceId: string) => {
        const { channelValues } = get();
        return channelValues[deviceId]?.isLoading || false;
      },

      getChannelError: (deviceId: string) => {
        const { channelValues } = get();
        return channelValues[deviceId]?.error || null;
      },

      getChannelLastUpdated: (deviceId: string) => {
        const { channelValues } = get();
        return channelValues[deviceId]?.lastUpdated || 0;
      },
    })),
    {
      name: "channels-store",
    }
  )
);
